import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { ContactInput } from "@/app/Contacts/Components/ContactInput";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { TaxInclusionInput } from "@/app/Payments/Components/TaxInclusionInput";
import { QuoteLineItemForm } from "@/app/Quotes/Components/QuoteLineItemForm";
import {
  CreateQuoteInput,
  Currency,
  TaxInclusionType,
  TaxType,
} from "@/gql/graphql";
import dayjs from "dayjs";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  createQuoteMutation,
  issueQuoteMutation,
} from "@/app/Quotes/GraphQL/quoteMutations";
import { toast } from "sonner";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { quoteValidation } from "@/app/Quotes/validations/quoteLineItemValidation";
import { useFormikContext } from "formik";
import { contactRecordQuery } from "@/app/Contacts/GraphQL/contactRecordQuery";
import { ExclamationTriangleIcon, PlusIcon } from "@heroicons/react/20/solid";
import { updateContactMutation } from "@/app/Contacts/GraphQL/contactMutations";
import { Button } from "@/components/catalyst/button";
import { useState } from "react";
import { Input } from "@/components/catalyst/input";

export function CreateQuoteDialog({
  initialValues,
  issueImmediately,
}: {
  initialValues?: Partial<CreateQuoteInput>;
  issueImmediately?: boolean;
}) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(createQuoteMutation);
  const { mutateAsync: issueQuote } = useGqlMutation(issueQuoteMutation);

  const currency = initialValues?.currency ?? Currency.Aud;
  const defaultTaxType =
    currency === Currency.Aud ? TaxType.Gst : TaxType.NoGst;

  return (
    <BaseDialog title="Create Quote">
      <GenericForm<CreateQuoteInput>
        validationSchema={toFormikValidationSchema(quoteValidation)}
        initialValues={{
          currency,
          quote_date: dayjs().format("YYYY-MM-DD"),
          expiry_date: dayjs().add(7, "days").format("YYYY-MM-DD"),
          tax_inclusion_type: TaxInclusionType.Exclusive,
          additional_notations: "",
          payee: {
            connect: null,
          },
          lineItems: {
            upsert: [
              {
                description: "",
                quantity: 1,
                tax_type: defaultTaxType,
                unit_amount: null!,
              },
            ],
          },
          ...initialValues,
        }}
        onSubmit={async (values) => {
          const res = await mutateAsync({
            input: values,
          });

          toast.success(`Quote created ${res.createQuote.reference}`);

          if (issueImmediately) {
            try {
              await issueQuote({
                id: res.createQuote.id,
              });
            } catch (error: unknown) {
              alert("An error occurred while issuing the quote");
              console.error(error);
            }
          }

          close();
        }}
      >
        <ContactInput name="payee.connect" label="Payee" />
        <CurrencyTypeInput name="currency" label="Currency" />
        <TaxInclusionInput name="tax_inclusion_type" label="Tax" />

        <div className="col-span-full">
          <BillingEmailCheck />
        </div>

        <FormDivider />
        <DateInput name="quote_date" label="Issue date" />
        <DateInput name="expiry_date" label="Expiry date" />

        <QuoteLineItemForm />

        <TextAreaInput
          name="additional_notations"
          label="Additional notations"
          className="col-span-full pt-6"
        />

        <DialogSubmitButton text={"Create Quote"} />
      </GenericForm>
    </BaseDialog>
  );
}

function BillingEmailCheck() {
  const { values } = useFormikContext<CreateQuoteInput>();
  const { mutateAsync, isPending } = useGqlMutation(updateContactMutation);
  const [email, setEmail] = useState<string>("");

  const { data } = useGqlQuery(
    contactRecordQuery,
    {
      id: values.payee.connect!,
    },
    {
      enabled: !!values.payee.connect,
    },
  );

  if (!data) {
    return null;
  }

  if (data.contact.billing_email) {
    return null;
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />

        <div className="flex-1">
          <div className="mb-2">
            <h4 className="text-sm font-medium text-amber-900">
              Billing Email Required
            </h4>
            <p className="mt-0.5 text-sm text-amber-700">
              Please add a billing email address for this contact
            </p>
          </div>

          <div className="flex space-x-3">
            <div>
              <Input
                name="email"
                className=""
                placeholder="Enter email address"
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
              />
            </div>

            <Button
              type="button"
              isLoading={isPending}
              LeadingIcon={PlusIcon}
              onClick={async () => {
                await mutateAsync({
                  id: data?.contact.id,
                  input: {
                    billing_email: email,
                  },
                });

                toast.success("Email added");

                setEmail("");
              }}
            >
              Add Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
